import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Landmark, Eye, EyeOff, User as UserIcon, Lock, LogIn, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ForgotPassword from "../components/ForgotPassword";
import { readCache, writeCache } from "../lib/brandCache";
import { TEMPLE_BG as BG } from "../lib/assets";


export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(() => readCache("pd_public_branding"));
  const [brandReady, setBrandReady] = useState(() => !!readCache("pd_public_branding"));
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    // A temple's own app opens /login?t=T001; remember it so later logins keep that temple's branding.
    const t = new URLSearchParams(window.location.search).get("t");
    if (t) writeCache("pd_login_temple", t.toUpperCase());
    const temple = t || readCache("pd_login_temple") || "";
    api.get("/public/branding", { params: temple ? { temple_id: temple } : {} })
      .then((r) => { setBranding(r.data); writeCache("pd_public_branding", r.data); })
      .catch(() => {}).finally(() => setBrandReady(true));
  }, []);
  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Welcome, ${u?.full_name || u?.username || username.trim()} — login successful`);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const title = branding?.temple_name || branding?.header_title || "Temple Management System";
  const titleTe = branding?.temple_name_telugu || "";
  const logoBlock = (size) => branding?.logo ? (
    <img src={branding.logo} alt="logo" className={`${size} object-contain rounded-full border-2 border-gold-400 bg-white p-1 shadow-lg`} data-testid="login-brand-logo" />
  ) : !brandReady ? (
    <div className={`${size} rounded-full border-2 border-gold-200 bg-gold-50`} />
  ) : (
    <div className={`${size} rounded-full bg-maroon-800 border-2 border-gold-400 flex items-center justify-center shadow-lg`} data-testid="login-gov-logo">
      <Landmark className="w-1/2 h-1/2 text-gold-400" />
    </div>
  );

  return (
    <div className="min-h-screen flex bg-cream" data-testid="login-page">
      {/* Desktop: temple photo panel */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Temple" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-maroon-950/90 via-maroon-900/60 to-maroon-900/30" />
        <div className="absolute bottom-0 p-10 text-white">
          <div className="gold-divider w-40 mb-4" />
          <h2 className="font-heading text-3xl font-bold leading-tight">{title}</h2>
          <p className="mt-3 text-gold-300 text-sm tracking-widest uppercase">Digital Temple Administration Platform</p>
        </div>
      </div>

      <div className="flex-1 relative flex flex-col items-center justify-center px-5 py-8 lg:p-6">
        <div className="hidden lg:block absolute inset-0 temple-header-pattern" />
        {/* Phones: full-screen temple photo behind the card */}
        <img src={BG} alt="" className="lg:hidden fixed inset-0 w-full h-full object-cover" />
        <div className="lg:hidden fixed inset-0 bg-gradient-to-b from-maroon-900/85 via-maroon-900/75 to-slate-950/90" />

        <div className="relative lg:hidden flex flex-col items-center text-center text-white mb-6">
          {logoBlock("w-24 h-24")}
          {titleTe && <p className="mt-4 font-telugu text-3xl text-gold-300 leading-tight drop-shadow">{titleTe}</p>}
          <h1 className={`${titleTe ? "mt-1" : "mt-4"} text-2xl font-bold leading-tight drop-shadow`} style={{ fontFamily: "system-ui, sans-serif" }}>{title}</h1>
          <p className="mt-1 text-sm text-gold-200 tracking-[0.25em] uppercase">{branding?.temple_name ? "Temple Management System" : "Digital Temple Administration Platform"}</p>
        </div>

        <div className="relative w-full max-w-md bg-white/95 backdrop-blur rounded-3xl lg:rounded-2xl shadow-2xl border border-white/60 lg:border-[#E5D9C5] card-gold-top px-6 py-7 sm:p-8" data-testid="login-card">
          <div className="hidden lg:flex justify-center mb-2">{logoBlock("w-16 h-16")}</div>
          <h2 className="text-center font-heading text-2xl font-bold text-maroon-900">User Login</h2>
          <p className="text-center text-xs uppercase tracking-[0.2em] text-gold-700 mb-6">Sign in to continue</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-amber-900">User Name</Label>
              <div className="relative mt-1">
                <UserIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username" autoComplete="username" autoCapitalize="none" required
                  className="h-12 pl-11 rounded-xl bg-[#FBF7EF] border-[#E5D9C5] text-base focus-visible:ring-maroon-600" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-amber-900">Password</Label>
              <div className="relative mt-1">
                <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input data-testid="login-password-input" type={showPassword ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password" required
                  className="h-12 pl-11 pr-11 rounded-xl bg-[#FBF7EF] border-[#E5D9C5] text-base focus-visible:ring-maroon-600" />
                <button type="button" data-testid="login-password-eye" onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-maroon-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end -mt-1">
              <button type="button" className="text-sm font-semibold text-maroon-700 hover:underline" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link">Forgot password?</button>
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-maroon-800 to-maroon-600 hover:from-maroon-900 hover:to-maroon-700 text-gold-100 font-heading text-base tracking-[0.2em] shadow-lg shadow-maroon-900/30">
              <LogIn className="w-5 h-5 mr-2" /> {loading ? "SIGNING IN…" : "LOGIN"}
            </Button>
          </form>
          {forgotOpen && <ForgotPassword open onClose={() => setForgotOpen(false)} />}
          <p className="mt-6 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-green-700" /> Authorized temple staff only. All activity is audited.
          </p>
        </div>

        <div className="relative mt-6 text-center text-white/75 lg:text-slate-500">
          <img src="/logo-192.png" alt="" className="w-8 h-8 mx-auto mb-1 object-contain lg:hidden" />
          <p className="text-xs">© {new Date().getFullYear()} developed by Aatreya Infotech</p>
        </div>
      </div>
    </div>
  );
}
